import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from "react";
import { IconX, IconCheck, IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";

type ToastType = "success" | "error" | "warning" | "info";

/** One button on a toast, e.g. Undo or Open. `key` is its data-action. */
export interface ToastAction {
  key: string;
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  /** How long it stays, in ms. Default 5000. */
  durationMs?: number;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

let toastId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = "info", options?: ToastOptions) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type, action: options?.action }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, options?.durationMs ?? 5000);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const icons: Record<ToastType, ReactNode> = {
    success: <IconCheck size={16} />,
    error: <IconAlertTriangle size={16} />,
    warning: <IconAlertTriangle size={16} />,
    info: <IconInfoCircle size={16} />,
  };

  const colors: Record<ToastType, string> = {
    success: "bg-green-600",
    error: "bg-red-600",
    warning: "bg-amber-500",
    info: "bg-primary",
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container */}
      {/* Above dialogs, and still pressable while one is open: a modal dialog turns
          pointer events off outside itself, which made Undo dead under the Share
          drawer (10 Oct). */}
      <div data-lilia-toast className="fixed bottom-4 right-4 z-[10002] flex flex-col gap-2 max-w-[360px]" style={{ pointerEvents: "auto" }}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            data-testid="toast"
            data-type={toast.type}
            role="status"
            className={`flex items-center gap-2 px-4 py-3 rounded-xl text-white text-sm shadow-lg ${colors[toast.type]}`}
            style={{ animation: "slideUp 200ms ease-out" }}
          >
            {icons[toast.type]}
            <span className="flex-1">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                data-testid="toast-action"
                data-action={toast.action.key}
                onClick={() => { dismiss(toast.id); toast.action!.onClick(); }}
                className="rounded px-2 py-0.5 font-semibold underline-offset-2 hover:bg-white/20 hover:underline"
              >
                {toast.action.label}
              </button>
            )}
            <button data-testid="toast-dismiss" onClick={() => dismiss(toast.id)} className="p-0.5 rounded hover:bg-white/20">
              <IconX size={14} />
            </button>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
